import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Activity, Coins, Search, UserCheck, Users, X } from "lucide-react";

import Header from "../components/HeaderV2";
import DatePicker from "../components/datepicker";
import { useKiosk } from "../KioskContext";
import { MemberType, useMember } from "../MemberContext";
import { PointType, usePoint } from "../PointContext";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/MemberStatusV2.module.css";

type ViewMode = "MEMBERS" | "ACCRUALS";
type MemberStatusFilter = "ALL" | "ACTIVE" | "DELETED";
type MemberSortKey = "LAST_ACTIVITY" | "POINT_DESC" | "POINT_ASC" | "EARNED_POINT" | "DEPOSIT_COUNT" | "JOINED_DESC" | "NAME_ASC";
type AccrualSortKey = "LATEST" | "OLDEST" | "POINT_DESC" | "INPUT_DESC";

type MemberPointSummary = {
    member: MemberType;
    currentPoint: number;
    depositCount: number;
    inputAmt: number;
    earnedPoint: number;
    lastActivityAt?: number;
    lastDepositAt?: number;
};

const startOfDay = (date: Date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

const endOfDay = (date: Date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
};

const isRealAccrual = (point: PointType) =>
    String(point.type || "") === "DEPOSIT" && point.owner !== "DONATE" && Number(point.inputAmt || 0) > 0;

const formatDateTime = (timestamp?: number) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatPhone = (phone?: string) => {
    const cleaned = `${phone || ""}`.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    return phone || "-";
};

const fmtYMD = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const MemberStatusV2 = () => {
    const { members } = useMember();
    const { points } = usePoint();
    const { kiosks } = useKiosk();
    const partnerId = sessionStorage.getItem("partnerId") || "-";

    const [mode, setMode] = useState<ViewMode>("MEMBERS");
    const [search, setSearch] = useState<string>("");
    const [memberStatus, setMemberStatus] = useState<MemberStatusFilter>("ALL");
    const [memberSort, setMemberSort] = useState<MemberSortKey>("LAST_ACTIVITY");
    const [accrualSort, setAccrualSort] = useState<AccrualSortKey>("LATEST");
    const [selectedMember, setSelectedMember] = useState<MemberPointSummary | null>(null);
    const [startDate, setStartDate] = useState<Date>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 29);
        return d;
    });
    const [endDate, setEndDate] = useState<Date>(new Date());

    const kioskMap = useMemo(
        () =>
            kiosks.reduce<Record<string, string>>((acc, kiosk) => {
                acc[kiosk.docId] = kiosk.name || kiosk.id || kiosk.docId;
                return acc;
            }, {}),
        [kiosks]
    );

    const memberMap = useMemo(
        () =>
            members.reduce<Record<string, MemberType>>((acc, member) => {
                acc[member.uid] = member;
                return acc;
            }, {}),
        [members]
    );

    const memberSummaries = useMemo(() => {
        const map = new Map<string, MemberPointSummary>();

        members.forEach((member) => {
            map.set(member.uid, {
                member,
                currentPoint: 0,
                depositCount: 0,
                inputAmt: 0,
                earnedPoint: 0,
            });
        });

        const latestPointByOwner = new Map<string, PointType>();

        points.forEach((point) => {
            if (!point.owner || point.owner === "DONATE") return;

            const summary = map.get(point.owner);
            if (!summary) return;

            const previous = latestPointByOwner.get(point.owner);
            if (!previous || Number(point.timestamp || 0) > Number(previous.timestamp || 0)) {
                latestPointByOwner.set(point.owner, point);
            }

            if (!summary.lastActivityAt || Number(point.timestamp || 0) > summary.lastActivityAt) {
                summary.lastActivityAt = point.timestamp;
            }

            if (isRealAccrual(point)) {
                summary.depositCount += 1;
                summary.inputAmt += Number(point.inputAmt || 0);
                summary.earnedPoint += Number(point.pointDelta || 0);
                if (!summary.lastDepositAt || Number(point.timestamp || 0) > summary.lastDepositAt) {
                    summary.lastDepositAt = point.timestamp;
                }
            }
        });

        latestPointByOwner.forEach((point, owner) => {
            const summary = map.get(owner);
            if (summary) summary.currentPoint = Number(point.pointAmt || 0);
        });

        return Array.from(map.values());
    }, [members, points]);

    const realAccrualsByOwner = useMemo(() => {
        const map = new Map<string, PointType[]>();
        points.filter(isRealAccrual).forEach((point) => {
            const rows = map.get(point.owner) || [];
            rows.push(point);
            map.set(point.owner, rows);
        });
        map.forEach((rows) => rows.sort((a, b) => Number(b.timestamp || 0) - Number(a.timestamp || 0)));
        return map;
    }, [points]);

    const s = startOfDay(startDate);
    const e = endOfDay(endDate);

    const realAccrualsInRange = useMemo(
        () =>
            points
                .filter(isRealAccrual)
                .filter((point) => Number(point.timestamp || 0) >= s && Number(point.timestamp || 0) <= e)
                .sort((a, b) => sortAccruals(a, b, accrualSort)),
        [points, s, e, accrualSort]
    );

    const filteredMembers = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        return memberSummaries
            .filter(({ member }) => {
                if (memberStatus === "ACTIVE") return member.deleted !== true;
                if (memberStatus === "DELETED") return member.deleted === true;
                return true;
            })
            .filter(({ member }) => {
                if (!keyword) return true;
                return [member.name, member.phone, formatPhone(member.phone), member.uid]
                    .filter(Boolean)
                    .some((value) => value.toLowerCase().includes(keyword));
            })
            .sort((a, b) => sortMembers(a, b, memberSort));
    }, [memberSort, memberStatus, memberSummaries, search]);

    const filteredAccruals = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        if (!keyword) return realAccrualsInRange;
        return realAccrualsInRange.filter((point) => {
            const member = memberMap[point.owner];
            const kioskName = kioskMap[point.kiosk] || point.kiosk || "";
            return [member?.name, member?.phone, formatPhone(member?.phone), point.owner, kioskName]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(keyword));
        });
    }, [kioskMap, memberMap, realAccrualsInRange, search]);

    const activeMembers = members.filter((member) => member.deleted !== true);
    const deletedMembers = members.length - activeMembers.length;
    const totalCurrentPoint = memberSummaries.reduce((sum, row) => sum + row.currentPoint, 0);
    const totalInputAmt = realAccrualsInRange.reduce((sum, point) => sum + Number(point.inputAmt || 0), 0);
    const totalEarnedPoint = realAccrualsInRange.reduce((sum, point) => sum + Number(point.pointDelta || 0), 0);

    const setPreset = (preset: "TODAY" | "LAST_7" | "LAST_30" | "THIS_MONTH") => {
        const today = new Date();
        if (preset === "TODAY") {
            setStartDate(new Date(startOfDay(today)));
            setEndDate(new Date(endOfDay(today)));
        }
        if (preset === "LAST_7") {
            const start = new Date(today);
            start.setDate(start.getDate() - 6);
            setStartDate(new Date(startOfDay(start)));
            setEndDate(new Date(endOfDay(today)));
        }
        if (preset === "LAST_30") {
            const start = new Date(today);
            start.setDate(start.getDate() - 29);
            setStartDate(new Date(startOfDay(start)));
            setEndDate(new Date(endOfDay(today)));
        }
        if (preset === "THIS_MONTH") {
            setStartDate(new Date(startOfDay(new Date(today.getFullYear(), today.getMonth(), 1))));
            setEndDate(new Date(endOfDay(today)));
        }
    };

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <div className={style["page-head"]}>
                        <div>
                            <h4 className={common["text--title"]}>회원 및 적립 현황</h4>
                            <p className={common["text--copy"]}>{partnerId} 회원별 현재 포인트와 실제 페트병 회수 적립 기록을 확인합니다.</p>
                        </div>
                    </div>

                    <div className={style["summary-grid"]}>
                        <SummaryCard title="전체 회원" value={members.length.toLocaleString()} unit="명" icon={<Users size={20} />} />
                        <SummaryCard title="정상 회원" value={activeMembers.length.toLocaleString()} unit="명" icon={<UserCheck size={20} />} />
                        <SummaryCard title="회원 보유 포인트" value={totalCurrentPoint.toLocaleString()} unit="P" icon={<Coins size={20} />} />
                        <SummaryCard
                            title="기간 내 실제 적립"
                            value={totalEarnedPoint.toLocaleString()}
                            unit="P"
                            icon={<Activity size={20} />}
                            caption={`${totalInputAmt.toLocaleString()}개 회수 · ${deletedMembers.toLocaleString()}명 탈퇴`}
                        />
                    </div>

                    <section className={style["filter-panel"]}>
                        <div className={style["panel-header"]}>
                            <div>
                                <p className={style["section-kicker"]}>조회 조건</p>
                                <h5 className={style["panel-title"]}>기간 설정</h5>
                                <p className={style["panel-copy"]}>
                                    {fmtYMD(startDate)} ~ {fmtYMD(endDate)} · 실제 적립 기준
                                </p>
                            </div>
                            <div className={style["mode-segment"]}>
                                <button className={mode === "MEMBERS" ? style["segment--active"] : ""} onClick={() => setMode("MEMBERS")}>
                                    회원 현황
                                </button>
                                <button className={mode === "ACCRUALS" ? style["segment--active"] : ""} onClick={() => setMode("ACCRUALS")}>
                                    적립 현황
                                </button>
                            </div>
                        </div>
                        <div className={style["preset-row"]}>
                            <button onClick={() => setPreset("TODAY")}>오늘</button>
                            <button onClick={() => setPreset("LAST_7")}>최근 7일</button>
                            <button onClick={() => setPreset("LAST_30")}>최근 30일</button>
                            <button onClick={() => setPreset("THIS_MONTH")}>이번 달</button>
                        </div>
                        <div className={style["wrapper--date-time"]}>
                            <DatePicker label="시작일" value={startDate} onChange={setStartDate} />
                            <DatePicker label="종료일" value={endDate} onChange={setEndDate} />
                        </div>
                    </section>

                    {mode === "MEMBERS" ? (
                        <MemberTable
                            rows={filteredMembers}
                            search={search}
                            setSearch={setSearch}
                            memberStatus={memberStatus}
                            setMemberStatus={setMemberStatus}
                            memberSort={memberSort}
                            setMemberSort={setMemberSort}
                            onOpen={setSelectedMember}
                        />
                    ) : (
                        <AccrualTable
                            rows={filteredAccruals}
                            memberMap={memberMap}
                            kioskMap={kioskMap}
                            search={search}
                            setSearch={setSearch}
                            accrualSort={accrualSort}
                            setAccrualSort={setAccrualSort}
                        />
                    )}
                </div>
            </div>
            {selectedMember && (
                <MemberHistoryModal
                    row={selectedMember}
                    accruals={realAccrualsByOwner.get(selectedMember.member.uid) || []}
                    kioskMap={kioskMap}
                    close={() => setSelectedMember(null)}
                />
            )}
        </>
    );
};

const sortMembers = (a: MemberPointSummary, b: MemberPointSummary, sort: MemberSortKey) => {
    if (sort === "POINT_DESC") return b.currentPoint - a.currentPoint;
    if (sort === "POINT_ASC") return a.currentPoint - b.currentPoint;
    if (sort === "EARNED_POINT") return b.earnedPoint - a.earnedPoint;
    if (sort === "DEPOSIT_COUNT") return b.depositCount - a.depositCount;
    if (sort === "JOINED_DESC") return Number(b.member.timestamp || 0) - Number(a.member.timestamp || 0);
    if (sort === "NAME_ASC") return (a.member.name || "").localeCompare(b.member.name || "", "ko");
    return Number(b.lastActivityAt || b.member.timestamp || 0) - Number(a.lastActivityAt || a.member.timestamp || 0);
};

const sortAccruals = (a: PointType, b: PointType, sort: AccrualSortKey) => {
    if (sort === "OLDEST") return Number(a.timestamp || 0) - Number(b.timestamp || 0);
    if (sort === "POINT_DESC") return Number(b.pointDelta || 0) - Number(a.pointDelta || 0);
    if (sort === "INPUT_DESC") return Number(b.inputAmt || 0) - Number(a.inputAmt || 0);
    return Number(b.timestamp || 0) - Number(a.timestamp || 0);
};

const SummaryCard = ({
    title,
    value,
    unit,
    icon,
    caption,
}: {
    title: string;
    value: string;
    unit: string;
    icon: ReactNode;
    caption?: string;
}) => (
    <div className={style["summary-card"]}>
        <div className={style["summary-icon"]}>{icon}</div>
        <p>{title}</p>
        <strong>
            {value}
            <span>{unit}</span>
        </strong>
        {caption && <small>{caption}</small>}
    </div>
);

const MemberTable = ({
    rows,
    search,
    setSearch,
    memberStatus,
    setMemberStatus,
    memberSort,
    setMemberSort,
    onOpen,
}: {
    rows: MemberPointSummary[];
    search: string;
    setSearch: (value: string) => void;
    memberStatus: MemberStatusFilter;
    setMemberStatus: (value: MemberStatusFilter) => void;
    memberSort: MemberSortKey;
    setMemberSort: (value: MemberSortKey) => void;
    onOpen: (row: MemberPointSummary) => void;
}) => (
    <section className={style["table-panel"]}>
        <PanelHeader title="회원 현황" description={`${rows.length.toLocaleString()}명 표시`} />
        <div className={style["toolbar"]}>
            <div className={style["search-wrap"]}>
                <Search size={16} />
                <input
                    className={common["input--kiosk-filter"]}
                    type="text"
                    placeholder="이름 또는 전화번호로 검색"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </div>
        </div>
        <div className={style["list-filter"]}>
            <span>상태</span>
            <button className={memberStatus === "ALL" ? style["list-filter--active"] : ""} onClick={() => setMemberStatus("ALL")}>
                전체
            </button>
            <button className={memberStatus === "ACTIVE" ? style["list-filter--active"] : ""} onClick={() => setMemberStatus("ACTIVE")}>
                정상
            </button>
            <button className={memberStatus === "DELETED" ? style["list-filter--active"] : ""} onClick={() => setMemberStatus("DELETED")}>
                탈퇴
            </button>
        </div>
        <div className={style["list-sort"]}>
            <button className={memberSort === "LAST_ACTIVITY" ? style["list-sort--active"] : ""} onClick={() => setMemberSort("LAST_ACTIVITY")}>
                최근 활동순
            </button>
            <button className={memberSort === "POINT_DESC" ? style["list-sort--active"] : ""} onClick={() => setMemberSort("POINT_DESC")}>
                포인트 높은순
            </button>
            <button className={memberSort === "POINT_ASC" ? style["list-sort--active"] : ""} onClick={() => setMemberSort("POINT_ASC")}>
                포인트 낮은순
            </button>
            <button className={memberSort === "EARNED_POINT" ? style["list-sort--active"] : ""} onClick={() => setMemberSort("EARNED_POINT")}>
                누적 적립순
            </button>
            <button className={memberSort === "DEPOSIT_COUNT" ? style["list-sort--active"] : ""} onClick={() => setMemberSort("DEPOSIT_COUNT")}>
                적립 횟수순
            </button>
            <button className={memberSort === "JOINED_DESC" ? style["list-sort--active"] : ""} onClick={() => setMemberSort("JOINED_DESC")}>
                최근 가입순
            </button>
            <button className={memberSort === "NAME_ASC" ? style["list-sort--active"] : ""} onClick={() => setMemberSort("NAME_ASC")}>
                이름순
            </button>
        </div>
        <div className={style["member-row-head"]}>
            <p>회원</p>
            <p>상태</p>
            <p>현재 포인트</p>
            <p>실제 적립</p>
            <p>마지막 적립</p>
        </div>
        <div className={style["table-list"]}>
            {rows.length === 0 && <EmptyState />}
            {rows.map((row) => {
                return (
                    <button className={style["member-row"]} key={row.member.uid} onClick={() => onOpen(row)}>
                        <div className={style["member-cell"]} data-label="회원">
                            <strong>{row.member.name || "이름 없음"}</strong>
                            <span>{formatPhone(row.member.phone)}</span>
                            <small>가입일 {formatDateTime(row.member.timestamp)}</small>
                        </div>
                        <p data-label="상태">
                            <StatusBadge deleted={row.member.deleted} />
                        </p>
                        <p data-label="현재 포인트" className={style["amount"]}>{row.currentPoint.toLocaleString()}P</p>
                        <p data-label="실제 적립">
                            {row.depositCount.toLocaleString()}건 · {row.earnedPoint.toLocaleString()}P
                            <span className={style["subtext"]}>{row.inputAmt.toLocaleString()}개 회수</span>
                        </p>
                        <p data-label="마지막 적립">{formatDateTime(row.lastDepositAt)}</p>
                    </button>
                );
            })}
        </div>
    </section>
);

const AccrualTable = ({
    rows,
    memberMap,
    kioskMap,
    search,
    setSearch,
    accrualSort,
    setAccrualSort,
}: {
    rows: PointType[];
    memberMap: Record<string, MemberType>;
    kioskMap: Record<string, string>;
    search: string;
    setSearch: (value: string) => void;
    accrualSort: AccrualSortKey;
    setAccrualSort: (value: AccrualSortKey) => void;
}) => (
    <section className={style["table-panel"]}>
        <PanelHeader title="실제 적립 현황" description={`${rows.length.toLocaleString()}건 표시`} />
        <div className={style["toolbar"]}>
            <div className={style["search-wrap"]}>
                <Search size={16} />
                <input
                    className={common["input--kiosk-filter"]}
                    type="text"
                    placeholder="회원 또는 키오스크로 검색"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </div>
        </div>
        <div className={style["list-sort"]}>
            <button className={accrualSort === "LATEST" ? style["list-sort--active"] : ""} onClick={() => setAccrualSort("LATEST")}>
                최신순
            </button>
            <button className={accrualSort === "OLDEST" ? style["list-sort--active"] : ""} onClick={() => setAccrualSort("OLDEST")}>
                오래된순
            </button>
            <button className={accrualSort === "POINT_DESC" ? style["list-sort--active"] : ""} onClick={() => setAccrualSort("POINT_DESC")}>
                적립 포인트 높은순
            </button>
            <button className={accrualSort === "INPUT_DESC" ? style["list-sort--active"] : ""} onClick={() => setAccrualSort("INPUT_DESC")}>
                회수량 많은순
            </button>
        </div>
        <div className={style["accrual-row-head"]}>
            <p>적립일시</p>
            <p>회원</p>
            <p>키오스크</p>
            <p>회수량</p>
            <p>적립 포인트</p>
            <p>잔액</p>
        </div>
        <div className={style["table-list"]}>
            {rows.length === 0 && <EmptyState />}
            {rows.map((point) => {
                const member = memberMap[point.owner];
                return (
                    <div className={style["accrual-row"]} key={point.id}>
                        <p data-label="적립일시">{formatDateTime(point.timestamp)}</p>
                        <div className={style["member-cell"]} data-label="회원">
                            <strong>{member?.name || "회원 정보 없음"}</strong>
                            <span>{formatPhone(member?.phone)}</span>
                            <small>{point.owner}</small>
                        </div>
                        <p data-label="키오스크">{kioskMap[point.kiosk] || point.kiosk || "-"}</p>
                        <p data-label="회수량">{Number(point.inputAmt || 0).toLocaleString()}개</p>
                        <p data-label="적립 포인트" className={style["amount"]}>{Number(point.pointDelta || 0).toLocaleString()}P</p>
                        <p data-label="잔액">{Number(point.pointAmt || 0).toLocaleString()}P</p>
                    </div>
                );
            })}
        </div>
    </section>
);

const PanelHeader = ({ title, description }: { title: string; description: string }) => (
    <div className={style["panel-header"]}>
        <div>
            <p className={style["section-kicker"]}>실시간 데이터</p>
            <h5>{title}</h5>
            <p>{description}</p>
        </div>
    </div>
);

const StatusBadge = ({ deleted }: { deleted: boolean }) => (
    <span className={[style["status-badge"], deleted ? style["status-badge--deleted"] : style["status-badge--active"]].join(" ")}>
        {deleted ? "탈퇴" : "정상"}
    </span>
);

const EmptyState = () => <div className={style["empty-state"]}>표시할 데이터가 없습니다.</div>;

const MemberHistoryModal = ({
    row,
    accruals,
    kioskMap,
    close,
}: {
    row: MemberPointSummary;
    accruals: PointType[];
    kioskMap: Record<string, string>;
    close: () => void;
}) => {
    const totalInputAmt = accruals.reduce((sum, point) => sum + Number(point.inputAmt || 0), 0);
    const totalEarnedPoint = accruals.reduce((sum, point) => sum + Number(point.pointDelta || 0), 0);

    return (
        <div className={[common["container--overlay"], common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], common["unit--overlay--active"], style["modal"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>회원 상세</p>
                        <h4 className={style["modal-title"]}>{row.member.name || "이름 없음"}</h4>
                        <p className={style["modal-copy"]}>
                            {formatPhone(row.member.phone)} · 가입일: {formatDateTime(row.member.timestamp)}
                        </p>
                    </div>
                    <button className={style["icon-button"]} onClick={close} aria-label="닫기">
                        <X size={18} />
                    </button>
                </div>

                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-summary-grid"]}>
                        <MiniMetric label="상태" value={row.member.deleted ? "탈퇴" : "정상"} />
                        <MiniMetric label="현재 포인트" value={`${row.currentPoint.toLocaleString()}P`} />
                        <MiniMetric label="전체 적립" value={`${totalEarnedPoint.toLocaleString()}P`} />
                        <MiniMetric label="전체 회수량" value={`${totalInputAmt.toLocaleString()}개`} />
                    </div>

                    <section className={style["history-panel"]}>
                        <PanelHeader title="적립 히스토리" description={`${accruals.length.toLocaleString()}건`} />
                        <div className={style["history-row-head"]}>
                            <p>적립일시</p>
                            <p>키오스크</p>
                            <p>회수량</p>
                            <p>적립 포인트</p>
                            <p>잔액</p>
                        </div>
                        <div className={style["history-list"]}>
                            {accruals.length === 0 && <EmptyState />}
                            {accruals.map((point) => (
                                <div className={style["history-row"]} key={point.id}>
                                    <p data-label="적립일시">{formatDateTime(point.timestamp)}</p>
                                    <p data-label="키오스크">{kioskMap[point.kiosk] || point.kiosk || "-"}</p>
                                    <p data-label="회수량">{Number(point.inputAmt || 0).toLocaleString()}개</p>
                                    <p data-label="적립 포인트" className={style["amount"]}>{Number(point.pointDelta || 0).toLocaleString()}P</p>
                                    <p data-label="잔액">{Number(point.pointAmt || 0).toLocaleString()}P</p>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

const MiniMetric = ({ label, value }: { label: string; value: string }) => (
    <div className={style["mini-metric"]}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

export default MemberStatusV2;
